import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadPurchasesUrl,
  zadSupplierPaymentByIdUrl,
  zadSupplierPaymentCancelUrl,
  zadSupplierPaymentPurchaseSummaryUrl,
  zadSupplierPaymentsUrl,
  zadSupplierPaymentSupplierSummaryUrl,
  zadSuppliersUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadSupplierPaymentWritePayload} from '../zad/paymentHelpers'
import {buildZadListQuery} from '../zad/query'
import {
  CancelZadSupplierPaymentRequest,
  CreateZadSupplierPaymentRequest,
  FetchZadPaymentPickerRequest,
  FetchZadPurchasePaymentSummaryRequest,
  FetchZadSupplierPaymentByIdRequest,
  FetchZadSupplierPaymentsRequest,
  FetchZadSupplierSummaryRequest,
} from './types'

export const fetchZadSupplierPayments = createAsyncThunk(
  'zadSupplierPayments/fetch',
  async (payload: FetchZadSupplierPaymentsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadSupplierPaymentsUrl}?${buildZadListQuery(payload)}`)
      const {payments, total, totalPages} = res.data || {}
      return {
        success: true as const,
        payments: Array.isArray(payments) ? payments : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSupplierPaymentById = createAsyncThunk(
  'zadSupplierPayments/fetchById',
  async (payload: FetchZadSupplierPaymentByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadSupplierPaymentByIdUrl(payload.id))
      return {
        success: true as const,
        payment: res.data.payment,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadSupplierPayment = createAsyncThunk(
  'zadSupplierPayments/create',
  async (payload: CreateZadSupplierPaymentRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadSupplierPaymentWritePayload(payload)
      const res = await axios.post(zadSupplierPaymentsUrl, body)
      return {
        success: true as const,
        payment: res.data.payment,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const cancelZadSupplierPayment = createAsyncThunk(
  'zadSupplierPayments/cancel',
  async (payload: CancelZadSupplierPaymentRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadSupplierPaymentCancelUrl(payload.id), {
        reason: String(payload.reason || ''),
      })
      return {
        success: true as const,
        payment: res.data.payment,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadSupplierAccountSummary = createAsyncThunk(
  'zadSupplierPayments/fetchSupplierSummary',
  async (payload: FetchZadSupplierSummaryRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadSupplierPaymentSupplierSummaryUrl(payload.supplierId))
      const data = res.data || {}
      return {
        success: true as const,
        summary: {
          supplier: data.supplier,
          validatedPurchasesTotal: data.validatedPurchasesTotal,
          paymentsTotal: data.paymentsTotal,
          outstanding: data.outstanding,
          purchaseCount: data.purchaseCount,
          paymentCount: data.paymentCount,
          ...(typeof data.purchaseNetHT === 'number' ? {purchaseNetHT: data.purchaseNetHT} : {}),
          ...(typeof data.purchaseVatTotal === 'number' ? {purchaseVatTotal: data.purchaseVatTotal} : {}),
          ...(typeof data.purchaseTTC === 'number' ? {purchaseTTC: data.purchaseTTC} : {}),
          ...(typeof data.purchaseStampTotal === 'number' ? {purchaseStampTotal: data.purchaseStampTotal} : {}),
          ...(typeof data.amountPayable === 'number' ? {amountPayable: data.amountPayable} : {}),
          ...(Array.isArray(data.purchases) ? {purchases: data.purchases} : {}),
        },
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPurchasePaymentSummary = createAsyncThunk(
  'zadSupplierPayments/fetchPurchaseSummary',
  async (payload: FetchZadPurchasePaymentSummaryRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadSupplierPaymentPurchaseSummaryUrl(payload.purchaseId))
      const data = res.data || {}
      return {
        success: true as const,
        summary: {
          purchaseId: String(data.purchaseId || payload.purchaseId),
          purchaseNumber: data.purchaseNumber,
          total: data.total,
          allocated: data.allocated,
          outstanding: data.outstanding,
          status: data.status,
          ...(typeof data.amountPayable === 'number' ? {amountPayable: data.amountPayable} : {}),
          ...(typeof data.netHT === 'number' ? {netHT: data.netHT} : {}),
          ...(typeof data.vatTotal === 'number' ? {vatTotal: data.vatTotal} : {}),
          ...(typeof data.totalTTC === 'number' ? {totalTTC: data.totalTTC} : {}),
          ...(typeof data.stampAmount === 'number' ? {stampAmount: data.stampAmount} : {}),
          ...(typeof data.supplierDocumentNumber === 'string'
            ? {supplierDocumentNumber: data.supplierDocumentNumber}
            : {}),
          ...(typeof data.purchaseDate === 'string' ? {purchaseDate: data.purchaseDate} : {}),
        },
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPaymentPickerSuppliers = createAsyncThunk(
  'zadSupplierPayments/fetchPickerSuppliers',
  async (payload: FetchZadPaymentPickerRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadSuppliersUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'name',
          sortOrder: 'asc',
          isActive: 'true',
          search: payload?.search || '',
        })}`
      )
      const {suppliers} = res.data || {}
      return {
        success: true as const,
        suppliers: Array.isArray(suppliers) ? suppliers : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadPaymentPickerPurchases = createAsyncThunk(
  'zadSupplierPayments/fetchPickerPurchases',
  async (payload: FetchZadPaymentPickerRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadPurchasesUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'purchaseDate',
          sortOrder: 'desc',
          supplier: payload.supplier || '',
          status: 'VALIDATED',
          search: payload.search || '',
        })}`
      )
      const {purchases} = res.data || {}
      return {
        success: true as const,
        purchases: Array.isArray(purchases) ? purchases : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
